/* eslint-disable boundaries/element-types */
import * as Tabs from '@radix-ui/react-tabs'
import { Button, message, Tooltip } from 'antd'
import { FeatureFormMode } from 'components/form/formTypes'
import { AssetEditorState } from 'components/rightDrawer/drawerStates/assetEditorState'
import {
  EditableFeatureForm,
  getFeatureTableOptionsByType,
} from 'features/assetDatabase'
import {
  AssetModelComponentClassMap,
  AssetModelFeatureClassMap,
  getJsonFileParser,
} from 'features/assetModeling'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import { useGeckoLocale } from 'features/locale'
import { useRolodexCache } from 'features/rolodex'
import { startCase } from 'lodash'
import React, { ChangeEvent, Fragment, useState } from 'react'
import styled from 'styled-components'
import {
  assetModelAvailableComponents,
  componentAvailableFeatures,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponent,
  GeckoComponentModelJSON,
  GeckoComponentModelType,
  GeckoEditableComponent,
  GeckoEditableFeature,
  organizeFeaturesByType,
  regenerateAssetModel,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'

import {
  EditFeatureTable,
  SelectedFeature,
} from './editRichAssetModelUtils/BaseFeatureTableComponents'

const StyledComponentButtonDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledGeckoAssetModelComponentButton = styled(Button)`
  width: 100%;
  height: 60px;
`

const StyledParagraph = styled.p`
  padding: 10px 0;
`

const StyledTabsList = styled(Tabs.List)`
  margin-bottom: 10px;
`

const StyledTabsTrigger = styled(Tabs.Trigger)`
  background-color: transparent;
  border: none;
  color: ${(props) =>
    props.disabled ? props.theme.forecast.text4 : props.theme.forecast.text1};
  width: 25%;

  &[data-state='active'] {
    border-bottom: 2px solid white;
    font-weight: bold;
  }
`

const StyledEditFeatureContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledReturnButton = styled(Button)`
  background-color: transparent;
  border: 1px solid grey;
`

interface EditRichAssetModelProps {
  assetModel: GeckoAssetModel
  component: GeckoComponent
  refreshData: () => Promise<void>
  goToEditComponent?: () => void
  goToSelectComponent?: () => void
  changeDrawerState?: (changeDrawerState: AssetEditorState) => void
  jsonSelector?: React.ReactNode
}

enum TabName {
  Info = 'info',
  Features = 'features',
}

export const EditGeckoRichAssetModel = ({
  assetModel,
  component,
  refreshData,
  goToEditComponent,
  goToSelectComponent,
  jsonSelector,
}: EditRichAssetModelProps) => {
  // Double check to make sure the Asset Model type is part of the supported list
  if (!Object.values(GeckoAssetModelType).includes(assetModel.data.type)) {
    throw new Error(
      `Unexpected Asset Model Type (${assetModel.data.type}). Not found in GeckoAssetModelType!`
    )
  }

  const [selectedComponentType, setSelectedComponentType] = useState<
    GeckoComponentModelType | undefined
  >(undefined)

  const [selectedFeature, setSelectedFeature] = useState<
    SelectedFeature | undefined
  >(undefined)

  const [activeTab, setActiveTab] = useState<TabName>(TabName.Info)
  const [jsonFile, setJsonFile] = useState(undefined)

  const { measurementSystem } = useGeckoLocale()
  const { permissions } = useGeckoAuth()
  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()
  const { clearAll } = useRolodexCache()

  // If this component hasn't been added to the rich asset model yet, and we can't
  // automatically determine what it is, ask the user
  if (!assetModel.data.components.has(component.id) && !selectedComponentType) {
    const availableComponents = () => {
      return assetModelAvailableComponents(assetModel.data.type).map(
        (component_type) => {
          const componentClass = AssetModelComponentClassMap.get(component_type)
          return (
            <Tooltip
              key={component_type}
              title={
                !componentClass
                  ? `This component type (${startCase(component_type)}) hasn't been defined as an editable component yet. Please reach out to the Enterprise team with any questions! `
                  : undefined
              }>
              <StyledGeckoAssetModelComponentButton
                key={component_type}
                onClick={() => {
                  setSelectedComponentType(
                    component_type as GeckoComponentModelType
                  )
                }}
                disabled={!componentClass}>
                {startCase(component_type)}
              </StyledGeckoAssetModelComponentButton>
            </Tooltip>
          )
        }
      )
    }

    return (
      <>
        <h4>Select Component Type</h4>
        <StyledParagraph>
          In order to edit this component, we need to define a rich asset model
          for it. The following options are available for a{' '}
          {startCase(assetModel.data.type)}:
        </StyledParagraph>
        <StyledComponentButtonDiv>
          {availableComponents()}
        </StyledComponentButtonDiv>
        <StyledParagraph>
          Don&apos;t see an appropriate component type here?{' '}
          <a
            href={'https://geckorobotics.slack.com/archives/C05SA4846Q4'}
            target={'_blank'}
            style={{ color: 'rebeccapurple' }}
            rel='noreferrer'>
            Reach out to the Enterprise team on Slack!
          </a>
        </StyledParagraph>
        {goToEditComponent && (
          <StyledParagraph>
            Not ready to make a rich asset model for this component?{' '}
            <a onClick={goToEditComponent}>
              Edit this component directly here.
            </a>
          </StyledParagraph>
        )}
      </>
    )
  } else if (
    assetModel.data.components.has(component.id) &&
    !selectedComponentType
  ) {
    const assetModelComponent = assetModel.data.components.get(component.id)
    if (assetModelComponent && assetModelComponent.type) {
      setSelectedComponentType(assetModelComponent.type)
    } else {
      throw new Error('Unexpected Component. No type defined!')
    }
    return <div>Loading...</div>
  }

  // By the time we reach this code, we should have a component type defined
  if (!selectedComponentType) {
    throw new Error(
      'Unexpected State. Asset Model does not contain the component, and no Component Type was selected!'
    )
  }

  const availableFeatureTypes = componentAvailableFeatures(
    selectedComponentType
  )

  const assetModelComponentsJSON = assetModel.raw.data.components as Record<
    string,
    GeckoComponentModelJSON
  >
  const selectedComponentJSON = assetModelComponentsJSON[component.id]

  const selectedComponentFeaturesJSON = organizeFeaturesByType(
    selectedComponentJSON,
    availableFeatureTypes
  )

  const handleFileUpload = (uploadEvent: ChangeEvent<HTMLInputElement>) => {
    // If event target is null, return early
    if (uploadEvent.target === null) {
      return
    }

    // If the event target has no files, then do nothing
    const uploadEventTarget = uploadEvent.target as HTMLInputElement
    if (uploadEventTarget.files === null) {
      return
    }

    const fileReader = new FileReader()
    // If no file was uploaded, reset the fastfield file and return early
    if (uploadEventTarget.files.length === 0) {
      setJsonFile(undefined)
      return
    }

    // Otherwise, try to parse the file
    fileReader.readAsText(uploadEventTarget.files[0], 'UTF-8')
    fileReader.onload = (fileEvent) => {
      // If the fileEvent is null, then we have nothing to parse
      if (fileEvent.target === null) {
        return
      }
      try {
        const parsedFile = JSON.parse(fileEvent.target.result as string)
        setJsonFile(parsedFile)
      } catch {
        messageApi.error("Unable to parse this file! Are you sure it's JSON?")
      }
    }
  }

  const deleteFeature = (featureID: string) => {
    const rawAssetModel = assetModel.raw
    const rawAssetModelComponents: Record<string, unknown> = rawAssetModel.data
      .components as Record<string, unknown>
    if (!rawAssetModelComponents[component.id]) {
      messageApi.error(
        'Unable to delete this feature. Could not find the Component ID on this Asset Model!'
      )
      return
    }

    const rawAssetModelComponent: Record<string, unknown> =
      rawAssetModelComponents[component.id] as Record<string, unknown>
    const rawAssetModelComponentFeatures: Record<string, unknown> =
      rawAssetModelComponent.features as Record<string, unknown>

    if (!rawAssetModelComponentFeatures[featureID]) {
      messageApi.error(
        'Unable to delete this feature. Could not find the Feature ID on this Component Model!'
      )
      return
    }

    delete rawAssetModelComponentFeatures[featureID]
    const regeneratedAssetModel = regenerateAssetModel(
      GeckoAssetModel.CreateFromJSON(rawAssetModel)
    )
    const rolodexPutEntry: RolodexEntryPutJson = {
      id: regeneratedAssetModel.id,
      type: RolodexEntryType.ASSET_MODEL,
      data: regeneratedAssetModel.data,
      tags: regeneratedAssetModel.tags,
    }
    handleSaveToRolodex([rolodexPutEntry])
  }

  const handleSaveToRolodex = (entries: RolodexEntryPutJson[]) => {
    rolodexPut(entries)
      .then(() => {
        return messageApi.success({
          content: 'Successfully saved Asset Model to Rolodex!',
          className: 'success-message',
        })
      })
      .catch(() => {
        return messageApi.error(
          'Failed to update the Asset Model in Rolodex! Please try again.'
        )
      })
      .finally(() => {
        clearAll()
        refreshData()
      })
  }
  // For each Feature Type we have, get the rendered table for that type
  const featureTables = availableFeatureTypes.map((featureType) => {
    const features = selectedComponentFeaturesJSON[featureType] ?? {}
    const featureClass = AssetModelFeatureClassMap.get(featureType) as
      | GeckoEditableFeature
      | undefined

    if (!featureClass) {
      throw new Error(
        `Unable to build Feature Edit Tables. Feature Type ${featureType} does not have a mapped class`
      )
    }

    const featureOptions = getFeatureTableOptionsByType(featureType)

    return (
      <Fragment key={featureType}>
        {contextHolder}
        {featureOptions && (
          <EditFeatureTable
            tableTitle={featureOptions.title}
            featureJSONMap={features}
            fieldsToShow={featureOptions.fields}
            featureClass={featureClass}
            featureType={featureType}
            sortBy={featureOptions.sortBy}
            selectFeature={setSelectedFeature}
            deleteFeature={deleteFeature}
            disableEditing={featureOptions.disableEditing}
            tableMessage={featureOptions.tableMessage}
          />
        )}
      </Fragment>
    )
  })

  // If we've selected a feature, early return with the EditableFeatureForm
  if (selectedFeature) {
    const { featureID, featureJSON, featureType } = selectedFeature

    const formMode = featureID ? FeatureFormMode.Edit : FeatureFormMode.Add

    return (
      <StyledEditFeatureContainer>
        <StyledReturnButton onClick={() => setSelectedFeature(undefined)}>
          Return to Feature Selection
        </StyledReturnButton>
        <EditableFeatureForm
          measurementSystem={measurementSystem}
          featureType={featureType}
          featureJSON={featureJSON}
          featureID={featureID}
          formMode={formMode}
          assetModel={assetModel}
          selectedComponent={component}
          onSave={(editedAssetModel) => {
            handleSaveToRolodex([editedAssetModel])
            setSelectedFeature(undefined)
          }}
          userPermissions={permissions}
          jsonSelector={jsonSelector}
        />
      </StyledEditFeatureContainer>
    )
  }

  const assetModelComponent = (assetModel.data.components.get(component.id) ??
    AssetModelComponentClassMap.get(
      selectedComponentType
    )) as GeckoEditableComponent
  const formMode = assetModel.data.components.get(component.id)
    ? FeatureFormMode.Edit
    : FeatureFormMode.Add

  if (!assetModelComponent) {
    throw new Error(
      'Unable to edit Asset Model Component, the class is undefined!'
    )
  }

  const componentJSON =
    formMode === FeatureFormMode.Edit
      ? (assetModelComponent.asJSON() as Record<string, unknown>)
      : undefined
  const componentID =
    formMode === FeatureFormMode.Edit
      ? (assetModelComponent.id as string)
      : undefined

  const jsonParserFunction = getJsonFileParser(selectedComponentType)

  // Otherwise, we should be displaying the feature selection page to the user
  return (
    <div>
      {contextHolder}
      <StyledEditFeatureContainer>
        {goToSelectComponent ? (
          <StyledReturnButton onClick={goToSelectComponent}>
            Return to Component Selection
          </StyledReturnButton>
        ) : null}
        <div>
          <h3>Editing Component: {assetModelComponent.name}</h3>
          <h4>Asset Model: {assetModel.id}</h4>
        </div>
        <Tabs.Root defaultValue={activeTab}>
          <StyledTabsList>
            <StyledTabsTrigger
              className={'TabsTrigger'}
              value={TabName.Info}
              onClick={() => setActiveTab(TabName.Info)}>
              Info
            </StyledTabsTrigger>
            <Tooltip
              title={
                !selectedComponentJSON
                  ? 'You must finish creating the component in the info section before moving to the features'
                  : undefined
              }
              placement={'bottomLeft'}>
              <StyledTabsTrigger
                className={'TabsTrigger'}
                value={TabName.Features}
                disabled={!selectedComponentJSON}
                onClick={() => setActiveTab(TabName.Features)}>
                Features
              </StyledTabsTrigger>
            </Tooltip>
          </StyledTabsList>
          <Tabs.Content value={TabName.Info}>
            <EditableFeatureForm
              measurementSystem={measurementSystem}
              featureType={selectedComponentType}
              featureJSON={componentJSON}
              featureID={componentID}
              formMode={formMode}
              assetModel={assetModel}
              selectedComponent={component}
              onSave={(editedAssetModel) => {
                // Keep the Component and RAM Component Model name the same
                const assetModelComponents = editedAssetModel.data
                  .components as Record<string, unknown>
                const ramComponent = assetModelComponents[
                  component.id
                ] as Record<string, unknown>
                if (!ramComponent) {
                  throw new Error(
                    'Unable to update component. Could not extract name from asset model!'
                  )
                }

                if (jsonParserFunction && jsonFile) {
                  ramComponent.features = jsonParserFunction(jsonFile)
                }

                const editedComponent = component.raw
                editedComponent.data.name = ramComponent.name as string
                handleSaveToRolodex([editedAssetModel, editedComponent])
              }}
              userPermissions={permissions}
              jsonSelector={jsonSelector}
            />
            {!!jsonParserFunction && !componentJSON && (
              <div style={{ paddingTop: '25px' }}>
                <p>
                  Optionally, when creating an asset model, you may upload a
                  JSON file to automatically enrich the asset model. Refer to
                  the documentation for the correct schema. After uploading the
                  file below, select &quot;Save&quot; button above.
                </p>
                <input type={'file'} onChange={handleFileUpload} />
              </div>
            )}
          </Tabs.Content>
          <Tabs.Content value={TabName.Features}>{featureTables}</Tabs.Content>
        </Tabs.Root>
      </StyledEditFeatureContainer>
    </div>
  )
}
